
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import TestimonialList from "@/components/TestimonialList";
import TestimonialTable, { SortField, SortDirection } from "@/components/TestimonialTable";
import TestimonialForm from "@/components/TestimonialForm";
import { LayoutGrid, LayoutList, Eye } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useNavigate } from "react-router-dom";

type Testimonial = Tables<"testimonials">;

const TestimonialManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("all");
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  // Check if user has permission to access this page
  if (!hasPermission("view:testimonials")) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">Access Denied</h2>
          <p className="mt-2 text-muted-foreground">You don't have permission to access this page.</p>
          <Button 
            className="mt-4" 
            onClick={() => navigate("/dashboard")}
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Fetch testimonials query with sorting
  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["testimonials", sortField, sortDirection],
    queryFn: async () => {
      let query = supabase
        .from("testimonials")
        .select("*");
      
      // Apply sorting if defined
      if (sortField && sortDirection) {
        query = query.order(sortField, { ascending: sortDirection === "asc" });
      } else {
        // Default sort
        query = query.order("created_at", { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }
      return data as Testimonial[];
    },
  });

  // Approve testimonial mutation
  const { mutate: approveTestimonial } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("testimonials")
        .update({ approved: true })
        .eq("id", id);

      if (error) throw new Error(error.message);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast({
        title: "Testimonial approved",
        description: "The testimonial has been approved and is now public.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to approve testimonial: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Delete testimonial mutation
  const { mutate: deleteTestimonial } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("testimonials")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast({
        title: "Testimonial deleted",
        description: "The testimonial has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete testimonial: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const filteredTestimonials = testimonials?.filter(testimonial => {
    if (activeTab === "all") return true;
    if (activeTab === "approved") return testimonial.approved;
    if (activeTab === "pending") return !testimonial.approved;
    return true;
  });

  const handleApprove = (id: string) => {
    approveTestimonial(id);
  };

  const handleDelete = (id: string) => {
    deleteTestimonial(id);
  };

  const handleEdit = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
    setIsCreating(true);
  };

  const handlePreview = (testimonial: Testimonial) => {
    // Navigate to the specific testimonial preview page
    navigate(`/testimonials/preview/${testimonial.id}`);
  };

  const handleSort = (field: SortField) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      // Cycle through: asc -> desc -> null -> asc
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection("asc"); // Changed from null to always have sorting
      } else {
        setSortDirection("asc");
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Testimonial Management</h1>
          <p className="text-muted-foreground">
            View, approve, edit, and manage customer testimonials
          </p>
        </div>
        <div className="flex space-x-2">
          {selectedTestimonial && (
            <Button 
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => handlePreview(selectedTestimonial)}
            >
              <Eye size={16} />
              Preview Selected
            </Button>
          )}
          <Button 
            onClick={() => {
              setSelectedTestimonial(null);
              setIsCreating(true);
            }}
            disabled={!hasPermission("manage:testimonials")}
          >
            Add New Testimonial
          </Button>
        </div>
      </div>

      <Separator />

      {isCreating ? (
        <Card>
          <CardHeader>
            <CardTitle>{selectedTestimonial ? "Edit Testimonial" : "Create New Testimonial"}</CardTitle>
            <CardDescription>
              {selectedTestimonial 
                ? "Update the existing testimonial" 
                : "Add a new customer testimonial to showcase on your website"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TestimonialForm 
              testimonial={selectedTestimonial} 
              onCancel={() => setIsCreating(false)}
              onSuccess={() => {
                setIsCreating(false);
                queryClient.invalidateQueries({ queryKey: ["testimonials"] });
              }}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList>
                <TabsTrigger value="all">All Testimonials</TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="pending">Pending Approval</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center space-x-2">
              <Button 
                variant={viewMode === "cards" ? "default" : "outline"} 
                size="icon" 
                onClick={() => setViewMode("cards")}
              >
                <LayoutGrid size={16} />
              </Button>
              <Button 
                variant={viewMode === "table" ? "default" : "outline"} 
                size="icon" 
                onClick={() => setViewMode("table")}
              >
                <LayoutList size={16} />
              </Button>
            </div>
          </div>
          
          <div className="mt-6">
            {viewMode === "cards" ? (
              <TestimonialList 
                testimonials={filteredTestimonials || []}
                isLoading={isLoading}
                onApprove={handleApprove}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onPreview={handlePreview}
              />
            ) : (
              <TestimonialTable 
                testimonials={filteredTestimonials || []}
                isLoading={isLoading}
                onApprove={handleApprove}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onPreview={handlePreview}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TestimonialManagement;
